/**
 * AudioRecorder Component
 *
 * Dual-mode audio recording for the demo chat:
 * - Desktop: Click mic to start → click stop button to send
 * - Mobile: Press-and-hold mic → release to send (WhatsApp-style)
 *
 * Real-time waveform visualization while recording.
 *
 * Validates: Requirements 8.1, 8.2, 8.3, 8.4, 8.6
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AudioRecorderProps {
  onRecordingComplete: (blob: Blob, mimeType: string) => void;
  onRecordingStart?: () => void;
  disabled?: boolean;
}

type RecorderState = 'idle' | 'recording';

// ─── MIME Type Preference ─────────────────────────────────────────────────────

const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

function getSupportedMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const mime of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return null;
}

// ─── Device Detection ─────────────────────────────────────────────────────────

function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

// ─── SVG Icons ────────────────────────────────────────────────────────────────

function MicIcon({ color = 'currentColor', size = 20 }: { color?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </svg>
  );
}

function StopIcon({ color = 'currentColor', size = 18 }: { color?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="6" width="12" height="12" rx="2" fill={color} />
    </svg>
  );
}

// ─── CSS Keyframes (injected once) ───────────────────────────────────────────

const KEYFRAMES_ID = 'audio-recorder-keyframes';

function ensureKeyframes(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(KEYFRAMES_ID)) return;
  const style = document.createElement('style');
  style.id = KEYFRAMES_ID;
  style.textContent = `
    @keyframes audio-recorder-pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(1.3); }
    }
    @keyframes mic-btn-glow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
      50% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
    }
  `;
  document.head.appendChild(style);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AudioRecorder({ onRecordingComplete, onRecordingStart, disabled }: AudioRecorderProps) {
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [isSupported, setIsSupported] = useState<boolean | null>(null);
  const [waveformBars, setWaveformBars] = useState<number[]>(new Array(20).fill(0));
  const [isMobile] = useState(() => isTouchDevice());

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mimeTypeRef = useRef<string>('');
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const isRecordingRef = useRef(false);

  // ─── Feature Detection ──────────────────────────────────────────────────

  useEffect(() => {
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setIsSupported(false);
      return;
    }
    const mime = getSupportedMimeType();
    if (!mime) {
      setIsSupported(false);
      return;
    }
    navigator.permissions?.query({ name: 'microphone' as PermissionName }).then((result) => {
      if (result.state === 'denied') {
        setIsSupported(false);
      } else {
        setIsSupported(true);
        mimeTypeRef.current = mime;
      }
    }).catch(() => {
      setIsSupported(true);
      mimeTypeRef.current = mime;
    });
  }, []);

  useEffect(() => {
    ensureKeyframes();
  }, []);

  // ─── Start Recording ────────────────────────────────────────────────────

  const startRecording = useCallback(async () => {
    if (disabled || isRecordingRef.current) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mime = mimeTypeRef.current || getSupportedMimeType();
      if (!mime) {
        stream.getTracks().forEach((t) => t.stop());
        setIsSupported(false);
        return;
      }
      mimeTypeRef.current = mime;

      const recorder = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;

        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
        if (audioContextRef.current) { void audioContextRef.current.close(); audioContextRef.current = null; }
        analyserRef.current = null;
        setWaveformBars(new Array(20).fill(0));

        isRecordingRef.current = false;
        setState('idle');
        setElapsed(0);

        if (blob.size > 0) {
          onRecordingComplete(blob, mime);
        }
      };

      recorder.start();
      isRecordingRef.current = true;
      setState('recording');
      setElapsed(0);
      onRecordingStart?.();

      // Analyser for waveform
      const audioCtx = new AudioContext();
      audioContextRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 64;
      source.connect(analyser);
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const NUM_BARS = 20;
      const updateBars = () => {
        analyser.getByteFrequencyData(dataArray);
        const step = Math.max(1, Math.floor(dataArray.length / NUM_BARS));
        const bars: number[] = [];
        for (let i = 0; i < NUM_BARS; i++) {
          bars.push(dataArray[i * step] / 255);
        }
        setWaveformBars(bars);
        animFrameRef.current = requestAnimationFrame(updateBars);
      };
      animFrameRef.current = requestAnimationFrame(updateBars);

      timerRef.current = setInterval(() => setElapsed((prev) => prev + 1), 1000);
    } catch {
      setIsSupported(false);
    }
  }, [disabled, onRecordingComplete, onRecordingStart]);

  // ─── Stop Recording ─────────────────────────────────────────────────────

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  // ─── Mobile: Press-and-hold handlers ────────────────────────────────────

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!isMobile) return;
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    void startRecording();
  }, [isMobile, startRecording]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isMobile) return;
    e.preventDefault();
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    stopRecording();
  }, [isMobile, stopRecording]);

  // ─── Desktop: Click to start, click stop to send ────────────────────────

  const handleDesktopClick = useCallback(() => {
    if (isMobile) return;
    if (state === 'idle') {
      void startRecording();
    }
  }, [isMobile, state, startRecording]);

  const handleDesktopStop = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  // ─── Cleanup on unmount ─────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (audioContextRef.current) void audioContextRef.current.close();
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  // ─── Render ─────────────────────────────────────────────────────────────

  if (isSupported === false || isSupported === null) return null;

  // Recording state — waveform + timer + stop button (desktop) or hint (mobile)
  if (state === 'recording') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 12px',
          flex: 1,
        }}
      >
        {/* Red dot */}
        <div
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: '#ef4444',
            animation: 'audio-recorder-pulse 1.2s ease-in-out infinite',
            flexShrink: 0,
          }}
        />

        {/* Waveform bars */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            height: 28,
            flex: 1,
            minWidth: 0,
          }}
          aria-label="Audio waveform"
        >
          {waveformBars.map((level, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                maxWidth: 4,
                borderRadius: 2,
                backgroundColor: '#ef4444',
                opacity: 0.5 + level * 0.5,
                height: `${Math.max(3, level * 28)}px`,
                transition: 'height 80ms ease-out',
              }}
            />
          ))}
        </div>

        {/* Timer */}
        <span
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 13,
            color: '#ef4444',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {formatElapsed(elapsed)}
        </span>

        {/* Desktop: Stop button to send */}
        {!isMobile && (
          <button
            type="button"
            onClick={handleDesktopStop}
            aria-label="Detener y enviar audio"
            title="Detener y enviar"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: 'none',
              background: '#ef4444',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'transform 100ms ease',
              animation: 'mic-btn-glow 2s ease-in-out infinite',
            }}
          >
            <StopIcon color="#ffffff" size={16} />
          </button>
        )}

        {/* Mobile: Release hint */}
        {isMobile && (
          <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>
            Soltar para enviar
          </span>
        )}
      </div>
    );
  }

  // Idle state — mic button
  // Mobile: press-and-hold behavior
  // Desktop: single click to start
  return (
    <button
      type="button"
      {...(isMobile
        ? {
            onPointerDown: handlePointerDown,
            onPointerUp: handlePointerUp,
            onPointerCancel: handlePointerUp,
          }
        : {
            onClick: handleDesktopClick,
          }
      )}
      onContextMenu={(e) => e.preventDefault()}
      disabled={disabled}
      aria-label={isMobile ? 'Mantener para grabar audio' : 'Grabar audio'}
      title={isMobile ? 'Mantener para grabar' : 'Grabar audio'}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 38,
        height: 38,
        borderRadius: '50%',
        border: 'none',
        background: disabled ? '#f1f5f9' : '#f8fafc',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        flexShrink: 0,
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        transition: 'background 150ms ease, transform 100ms ease',
      }}
    >
      <MicIcon color={disabled ? '#94a3b8' : '#64748b'} size={20} />
    </button>
  );
}
